import React, { useState, useEffect, useRef, useContext } from "react";
import { getApiUrl } from "../utils/api";
import { FaTimes, FaSync, FaPaperPlane, FaUser, FaArrowLeft } from "react-icons/fa";
import { useChat } from "../context/ChatContext";
import { UserContext } from "../context/UserContext";
import LoadingSpinner from "./LoadingSpinner";
import { useToast } from "../context/ToastContext";
import { useNavigate } from "react-router-dom";
import { getAvatarUrl as resolveAvatarUrl, DEFAULT_AVATAR_DATA_URL, FALLBACK_AVATAR_SVG } from "../utils/avatar";
import "./ChatModal.css";

const ChatModal = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    conversations,
    conversationsPagination,
    loadingMoreConversations,
    loadMoreConversations,
    currentConversation,
    messages,
    messagesHasMore,
    loadingOlderMessages,
    loadOlderMessages,
    selectConversation,
    sendMessage,
    fetchMessages,
    fetchConversations,
    createConversation,
    isChatModalOpen,
    chatModalPreSelectedUser,
    closeChatModal,
    markAsRead,
    leaveConversation,
    typingUsers,
    setTyping,
    isUserTypingActive,
    conversationSeenAtMap,
    conversationSeenMessageIdMap,
    conversationDeliveredMessageIdMap,
  } = useChat();

  const { user } = useContext(UserContext);

  const [messageText, setMessageText] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const modalRef = useRef(null);
  const typingDebounceRef = useRef(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const hasHandledPreSelectedUser = useRef(false);
  const [mobilePanel, setMobilePanel] = useState("list");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 639px)").matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const syncMobile = () => setIsMobile(mediaQuery.matches);
    syncMobile();
    mediaQuery.addEventListener("change", syncMobile);
    return () => mediaQuery.removeEventListener("change", syncMobile);
  }, []);

  useEffect(() => {
    if (!isChatModalOpen) {
      setMobilePanel("list");
      return;
    }
    if (chatModalPreSelectedUser || currentConversation) {
      setMobilePanel("chat");
    } else {
      setMobilePanel("list");
    }
  }, [isChatModalOpen, chatModalPreSelectedUser, currentConversation]);

  const handleClose = async () => {
    try {
      const convId = currentConversation?.conversationId;
      if (convId && typeof convId === "string" && !convId.startsWith("temp-")) {
        // Ensure server state is cleared before we refresh on close (prevents 1-unread flicker/stickiness)
        await markAsRead(convId);
      }
    } catch (_) {
      // ignore close-time errors
    } finally {
      try {
        leaveConversation();
      } catch (_) {}
      closeChatModal();
    }
  };

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "instant",
      block: "end",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup typing indicator when conversation changes or unmounts
  useEffect(() => {
    return () => {
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
        typingDebounceRef.current = null;
      }
      if (currentConversation?.conversationId) {
        setTyping(currentConversation.conversationId, false);
      }
    };
  }, [currentConversation, setTyping]);

  // Tick every 300ms while modal open to evaluate typing window
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 300);
    return () => clearInterval(t);
  }, []);

  // Keep the composer above the mobile keyboard; reset fully when keyboard closes
  useEffect(() => {
    if (!isChatModalOpen) return undefined;

    const modal = modalRef.current;
    const viewport = window.visualViewport;
    if (!modal || !viewport) return undefined;

    const isMobileViewport = () =>
      window.matchMedia("(max-width: 639px)").matches;

    const clearKeyboardAdjustments = () => {
      modal.classList.remove("chat-modal--keyboard-adjusted");
      modal.style.removeProperty("--chat-vv-top");
      modal.style.removeProperty("--chat-vv-height");
      modal.style.removeProperty("top");
      modal.style.removeProperty("height");
      modal.style.removeProperty("max-height");
      modal.style.removeProperty("maxHeight");
      modal.style.removeProperty("transform");
      modal.style.removeProperty("bottom");
    };

    const syncMobileViewport = () => {
      if (!isMobileViewport()) {
        clearKeyboardAdjustments();
        return;
      }

      const layoutHeight = window.innerHeight;
      const visibleHeight = viewport.height;
      const offsetTop = Math.max(0, viewport.offsetTop);
      const keyboardGap = layoutHeight - visibleHeight - offsetTop;
      const keyboardOpen =
        keyboardGap > 60 ||
        offsetTop > 8 ||
        visibleHeight < layoutHeight * 0.72;

      if (keyboardOpen) {
        modal.classList.add("chat-modal--keyboard-adjusted");
        modal.style.setProperty("--chat-vv-top", `${offsetTop}px`);
        modal.style.setProperty("--chat-vv-height", `${visibleHeight}px`);
      } else {
        clearKeyboardAdjustments();
      }
    };

    const scheduleSync = () => {
      syncMobileViewport();
      window.requestAnimationFrame(syncMobileViewport);
    };

    const handleFocusOut = (event) => {
      const next = event.relatedTarget;
      if (next && modal.contains(next)) return;
      window.setTimeout(scheduleSync, 50);
      window.setTimeout(scheduleSync, 200);
      window.setTimeout(scheduleSync, 450);
    };

    scheduleSync();
    viewport.addEventListener("resize", scheduleSync);
    viewport.addEventListener("scroll", scheduleSync);
    window.addEventListener("resize", scheduleSync);
    window.addEventListener("focusout", handleFocusOut, true);

    return () => {
      viewport.removeEventListener("resize", scheduleSync);
      viewport.removeEventListener("scroll", scheduleSync);
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("focusout", handleFocusOut, true);
      clearKeyboardAdjustments();
    };
  }, [isChatModalOpen]);

  // Typing indicator visibility driven by isUserTypingActive; no extra local timers here

  // Immediate scroll when conversation changes
  useEffect(() => {
    if (currentConversation && messages.length > 0) {
      // Use immediate scroll when conversation first loads
      setTimeout(() => scrollToBottom(false), 100);
    }
  }, [currentConversation]);

  // Handle pre-selected user when modal opens - create temp conversation immediately
  useEffect(() => {
    if (
      isChatModalOpen &&
      chatModalPreSelectedUser &&
      !currentConversation &&
      !hasHandledPreSelectedUser.current
    ) {
      hasHandledPreSelectedUser.current = true;

      // Create temp conversation immediately so input appears right away
      const tempConversation = {
        conversationId: `temp-${Date.now()}`,
        otherUser: {
          _id: chatModalPreSelectedUser._id,
          username: chatModalPreSelectedUser.username,
          name: chatModalPreSelectedUser.name,
          avatar: chatModalPreSelectedUser.avatar,
        },
        unreadCount: 0,
      };
      
      // Set temp conversation immediately so input appears
      selectConversation(tempConversation);
      setSelectedConversation(tempConversation);

      // In parallel, fetch conversations to see if a real one already exists
      fetchConversations();
    }
  }, [
    isChatModalOpen,
    chatModalPreSelectedUser,
    currentConversation,
    selectConversation,
    fetchConversations,
  ]);

  // Fetch conversations when modal opens
  useEffect(() => {
    if (isChatModalOpen) {
      fetchConversations();
    }
  }, [isChatModalOpen, fetchConversations]);

  // Watch for conversation updates when we have a temp conversation
  // and replace it with a real one if found
  useEffect(() => {
    if (
      currentConversation?.conversationId?.startsWith('temp-') &&
      chatModalPreSelectedUser &&
      conversations.length > 0
    ) {
      const existingConversation = conversations.find(
        (conv) =>
          conv.otherUser && conv.otherUser._id === chatModalPreSelectedUser._id
      );

      if (existingConversation) {
        // Replace temp conversation with real one
        selectConversation(existingConversation);
        setSelectedConversation(existingConversation);
      }
    }
  }, [conversations, currentConversation, chatModalPreSelectedUser, selectConversation]);

  // Reset pre-selected user handling when modal closes
  useEffect(() => {
    if (!isChatModalOpen) {
      hasHandledPreSelectedUser.current = false;
      // Refresh conversations on close; ChatContext derives unread badge from it
      fetchConversations();
    }
  }, [isChatModalOpen, fetchConversations]);

  const handleCreateAndSelectConversation = async () => {
    if (!chatModalPreSelectedUser || isCreatingConversation) return;

    try {
      setIsCreatingConversation(true);
      setIsLoadingMessages(true);
      
      // Create temporary conversation object to show input immediately
      const tempConversation = {
        conversationId: `temp-${Date.now()}`,
        otherUser: {
          _id: chatModalPreSelectedUser._id,
          username: chatModalPreSelectedUser.username,
          name: chatModalPreSelectedUser.name,
          avatar: chatModalPreSelectedUser.avatar,
        },
        unreadCount: 0,
      };
      
      // Set temporary conversation immediately so input appears
      // selectConversation will now handle temp conversations properly (skip message fetch)
      await selectConversation(tempConversation);
      setSelectedConversation(tempConversation);
      
      const conversationId = await createConversation(
        chatModalPreSelectedUser._id
      );

      // Wait a moment for the conversation to be created
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Fetch conversations to get the updated list
      await fetchConversations();

      // Get the updated conversations list (state might not be updated yet)
      // So we fetch again and use the returned data
      const token = (typeof window !== "undefined" && localStorage.getItem("token")) || null;
      const response = await fetch(
        getApiUrl("/api/chat/conversations"),
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (response.ok) {
        const fetchedConversations = await response.json();
        
        // Find the newly created conversation in the fetched list
        const newConversation = fetchedConversations.find(
          (conv) => conv.conversationId === conversationId
        );

        if (newConversation) {
          await handleSelectConversation(newConversation);
        } else {
          // If still not found, try one more time
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const retryResponse = await fetch(
            getApiUrl("/api/chat/conversations"),
            {
              credentials: "include",
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
          if (retryResponse.ok) {
            const retryConversations = await retryResponse.json();
            const retryConversation = retryConversations.find(
              (conv) => conv.conversationId === conversationId
            );
            if (retryConversation) {
              await handleSelectConversation(retryConversation);
            } else {
              setIsLoadingMessages(false);
              console.error("Failed to find created conversation");
            }
          }
        }
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      setIsLoadingMessages(false);
      const errorMessage = error.message || "Unable to message this user";
      showToast({ type: "error", message: errorMessage });
      // Keep temp conversation so user can still try to send a message
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const getAvatarUrl = (avatar) => resolveAvatarUrl(avatar);

  const handleSelectConversation = async (conversation) => {
    if (!conversation) {
      setIsLoadingMessages(false);
      return;
    }

    try {
      setIsLoadingMessages(true);
      setSelectedConversation(conversation);
      await selectConversation(conversation);
      if (isMobile) {
        setMobilePanel("chat");
      }

      // Scroll to bottom after loading messages
      setTimeout(() => scrollToBottom(false), 200);
    } catch (error) {
      console.error("Error selecting conversation:", error);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleMobileBackToList = () => {
    leaveConversation();
    setSelectedConversation(null);
    setMobilePanel("list");
  };

  const renderConversationList = () => (
    <>
      <div className="chat-modal__sidebar-head">
        <h4 className="chat-modal__sidebar-title">Messages</h4>
      </div>
      <p className="chat-modal__sidebar-label">Chats</p>
      {conversations.length === 0 ? (
        <p className="chat-modal__empty-list">No conversations yet</p>
      ) : (
        <div className="chat-modal__conversation-list">
          {conversations.map((conversation) => {
            const otherParticipant =
              conversation.otherUser || {
                _id: null,
                username: "deleted",
                avatar: "",
              };
            const isDeletedUser = !conversation?.otherUser?._id;
            const isSelected =
              selectedConversation?.conversationId ===
              conversation.conversationId;

            return (
              <button
                type="button"
                key={conversation.conversationId}
                onClick={() =>
                  !isLoadingMessages &&
                  !isCreatingConversation &&
                  !isDeletedUser &&
                  handleSelectConversation(conversation)
                }
                disabled={
                  isLoadingMessages || isCreatingConversation || isDeletedUser
                }
                className={`chat-modal__conversation-item${
                  isSelected ? " chat-modal__conversation-item--active" : ""
                }`}
              >
                {otherParticipant?.avatar ? (
                  <img
                    src={getAvatarUrl(otherParticipant.avatar)}
                    alt={otherParticipant.username}
                    className="chat-modal__conversation-avatar"
                  />
                ) : (
                  <div className="chat-modal__conversation-avatar chat-modal__conversation-avatar--fallback">
                    <FaUser className="w-3.5 h-3.5 text-gray-600" />
                  </div>
                )}
                <div className="chat-modal__conversation-meta">
                  <span className="chat-modal__conversation-name">
                    {isDeletedUser
                      ? "Deleted user"
                      : otherParticipant?.username || "Unknown"}
                  </span>
                  {conversation.lastMessage?.content && (
                    <span className="chat-modal__conversation-preview">
                      {conversation.lastMessage.content}
                    </span>
                  )}
                </div>
                {conversation.unreadCount > 0 && (
                  <span className="chat-modal__conversation-unread">
                    {conversation.unreadCount}
                  </span>
                )}
              </button>
            );
          })}
          {conversationsPagination?.hasNextPage && (
            <div className="px-3 py-2 text-center">
              <button
                type="button"
                className="text-xs font-semibold text-[#f26522] hover:underline disabled:opacity-60"
                disabled={loadingMoreConversations}
                onClick={() => loadMoreConversations()}
              >
                {loadingMoreConversations
                  ? "Loading..."
                  : "Load more conversations"}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    const recipientId = currentConversation?.otherUser?._id || chatModalPreSelectedUser?._id;

    if (!recipientId) {
      showToast({ type: "error", message: "This user was deleted. You can’t message them." });
      return;
    }

    const tempMessage = messageText;
    setMessageText("");

    const convId = currentConversation?.conversationId;
    if (convId && !convId.startsWith("temp-")) {
      setTyping(convId, false);
    }
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
      typingDebounceRef.current = null;
    }

    try {
      await sendMessage(recipientId, tempMessage);

      if (currentConversation?.conversationId?.startsWith("temp-")) {
        fetchConversations();
      }

      window.requestAnimationFrame(() => {
        scrollToBottom(true);
        messageInputRef.current?.focus();
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageText(tempMessage);
      const errorMessage = error.message || "Unable to message this user";
      showToast({ type: "error", message: errorMessage });
    }
  };

  const handleRefreshMessages = async () => {
    if (currentConversation) {
      setIsLoadingMessages(true);
      await fetchMessages(currentConversation.conversationId);
      setIsLoadingMessages(false);
      // Scroll to bottom after refresh
      setTimeout(() => scrollToBottom(false), 100);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const startOfMsg = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const dayDiff = Math.round(
      (startOfToday - startOfMsg) / (1000 * 60 * 60 * 24)
    );

    if (dayDiff === 0) return "Today";
    if (dayDiff === 1) return "Yesterday";
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDayKey = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  if (!isChatModalOpen) return null;

  const showMobileBack =
    isMobile && mobilePanel === "chat" && (currentConversation || chatModalPreSelectedUser);
  const headerTitle =
    isMobile && mobilePanel === "list"
      ? "Messages"
      : (currentConversation && currentConversation.otherUser?.username) ||
        chatModalPreSelectedUser?.username ||
        "Chat";

  return (
    <div className="chat-modal-overlay fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-30 z-[9998]"
        onClick={closeChatModal}
      ></div>

      {/* Chat Modal */}
      <div
        ref={modalRef}
        className={`chat-modal chat-modal--mobile-${mobilePanel}`}
      >
        {/* Header */}
        <div className="chat-modal__header">
          <div className="chat-modal__header-main">
            {showMobileBack && (
              <button
                type="button"
                onClick={handleMobileBackToList}
                className="chat-modal__back"
                aria-label="Back to conversations"
              >
                <FaArrowLeft className="w-4 h-4" />
              </button>
            )}
            {(mobilePanel === "chat" &&
              ((currentConversation && currentConversation.otherUser) ||
                chatModalPreSelectedUser)) ? (
              <>
                {((currentConversation && currentConversation.otherUser?.avatar) ||
                  chatModalPreSelectedUser?.avatar) ? (
                  <img
                    src={getAvatarUrl(
                      (currentConversation && currentConversation.otherUser?.avatar) ||
                        chatModalPreSelectedUser?.avatar
                    )}
                    alt={
                      (currentConversation && currentConversation.otherUser?.username) ||
                      chatModalPreSelectedUser?.username ||
                      "User"
                    }
                    className="chat-modal__header-avatar"
                    onError={(e) => {
                      e.currentTarget.src =
                        e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                          ? FALLBACK_AVATAR_SVG
                          : DEFAULT_AVATAR_DATA_URL;
                    }}
                  />
                ) : (
                  <div className="chat-modal__header-avatar chat-modal__header-avatar--fallback">
                    <FaUser className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="chat-modal__header-text">
                  <h3
                    className="chat-modal__header-title chat-modal__header-title--link"
                    onClick={() => {
                      const username =
                        (currentConversation && currentConversation.otherUser?.username) ||
                        chatModalPreSelectedUser?.username;
                      if (username) {
                        navigate(`/users/${username}`);
                        handleClose();
                      }
                    }}
                  >
                    {headerTitle}
                  </h3>
                  {currentConversation && currentConversation.otherUser?.name && (
                    <div className="chat-modal__header-subtitle">
                      {currentConversation.otherUser.name}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <h3 className="chat-modal__header-title">{headerTitle}</h3>
            )}
          </div>
          <div className="chat-modal__header-actions">
            {currentConversation && mobilePanel === "chat" && (
              <button
                type="button"
                onClick={handleRefreshMessages}
                className="chat-modal__icon-btn"
                title="Refresh messages"
                aria-label="Refresh messages"
              >
                <FaSync className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="chat-modal__icon-btn"
              aria-label="Close chat"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="chat-modal__body">
          <div className="chat-modal__sidebar">{renderConversationList()}</div>

          {/* Chat Area */}
          <div className="chat-modal__chat">
            {currentConversation || chatModalPreSelectedUser ? (
              <>
                {/* Messages */}
                <div className="chat-modal__messages space-y-3 bg-white">
                  {messagesHasMore && (
                    <div className="flex justify-center py-1">
                      <button
                        type="button"
                        className="text-xs font-semibold text-[#f26522] hover:underline disabled:opacity-60"
                        disabled={loadingOlderMessages || isLoadingMessages}
                        onClick={async () => {
                          if (!currentConversation?.conversationId) return;
                          const el = document.querySelector(
                            ".chat-modal__messages"
                          );
                          const prevHeight = el?.scrollHeight || 0;
                          await loadOlderMessages(
                            currentConversation.conversationId
                          );
                          requestAnimationFrame(() => {
                            if (!el) return;
                            el.scrollTop = el.scrollHeight - prevHeight;
                          });
                        }}
                      >
                        {loadingOlderMessages
                          ? "Loading..."
                          : "Load earlier messages"}
                      </button>
                    </div>
                  )}
                  {isLoadingMessages && messages.length === 0 && !isCreatingConversation ? (
                    <div className="flex justify-center items-center h-full">
                      <LoadingSpinner />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-500 text-sm">
                      {isCreatingConversation 
                        ? "Creating conversation..." 
                        : "No messages yet. Start the conversation!"}
                    </div>
                  ) : (
                    <>
                      {messages.map((message, idx) => {
                        const isOwnMessage =
                          user && message.sender._id === user._id;
                        const seenMessageId = conversationSeenMessageIdMap[currentConversation?.conversationId];
                        const deliveredMessageId = conversationDeliveredMessageIdMap[currentConversation?.conversationId];
                        const isThisMessage = String(message._id) === String(seenMessageId);
                        const isThisDelivered = String(message._id) === String(deliveredMessageId);
                        const showSeen = !!(isOwnMessage && seenMessageId && isThisMessage);
                        const showDelivered = !!(isOwnMessage && !showSeen && deliveredMessageId && isThisDelivered);
                        const dayKey = getDayKey(message.createdAt);
                        const prevDayKey =
                          idx > 0 ? getDayKey(messages[idx - 1].createdAt) : null;
                        const showDateSeparator = dayKey && dayKey !== prevDayKey;
                        return (
                          <React.Fragment key={message._id}>
                            {showDateSeparator && (
                              <div className="chat-modal__date-separator" role="separator">
                                <span className="chat-modal__date-separator-label">
                                  {formatMessageDate(message.createdAt)}
                                </span>
                              </div>
                            )}
                          <div
                            className={`chat-modal__message ${
                              isOwnMessage ? "chat-modal__message--own" : ""
                            }`}
                          >
                            {!isOwnMessage && (
                              message.sender?.avatar ? (
                                <img
                                  src={getAvatarUrl(message.sender.avatar)}
                                  alt={message.sender.username}
                                  className="chat-modal__avatar"
                                />
                              ) : (
                                <div className="chat-modal__avatar--fallback">
                                  <FaUser className="w-3 h-3 text-gray-600" />
                                </div>
                              )
                            )}
                            <div
                              className={`chat-modal__bubble ${
                                isOwnMessage
                                  ? "chat-modal__bubble--own"
                                  : "chat-modal__bubble--other"
                              }`}
                            >
                              <p className="whitespace-pre-wrap">{message.content}</p>
                              <p
                                className={`text-[11px] mt-1 text-right ${
                                  isOwnMessage ? "text-white/80" : "text-gray-500"
                                }`}
                              >
                                {formatTime(message.createdAt)}
                              </p>
                              {showSeen && (
                                <div className="text-[10px] mt-0.5 text-right opacity-80">
                                  Seen
                                </div>
                              )}
                              {showDelivered && (
                                <div className="text-[10px] mt-0.5 text-right opacity-80">
                                  Delivered
                                </div>
                              )}
                            </div>
                            {isOwnMessage && (
                              user?.avatar ? (
                                <img
                                  src={getAvatarUrl(user.avatar)}
                                  alt={user.username}
                                  className="chat-modal__avatar"
                                />
                              ) : (
                                <div className="chat-modal__avatar--fallback">
                                  <FaUser className="w-3 h-3 text-gray-600" />
                                </div>
                              )
                            )}
                          </div>
                          </React.Fragment>
                        );
                      })}
                      {isLoadingMessages && (
                        <div className="flex justify-center">
                          <div className="text-gray-500 text-xs">
                            Loading...
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Typing indicator positioned just above the input */}
                {currentConversation?.otherUser?._id && isUserTypingActive(currentConversation.otherUser._id) && (
                  <div className="px-3 py-2 flex justify-center bg-white">
                    <div className="inline-flex items-center gap-2 text-xs text-gray-700 bg-gray-50 rounded-full px-3 py-1.5 shadow-sm">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse"></span>
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse [animation-delay:150ms]"></span>
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse [animation-delay:300ms]"></span>
                      </span>
                      <span>{currentConversation.otherUser.username} is typing…</span>
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="chat-modal__composer">
                  <div className="chat-modal__composer-row">
                    <label htmlFor="message-input" className="sr-only">
                      Type a message
                    </label>
                    <input
                      ref={messageInputRef}
                      id="message-input"
                      type="text"
                      value={messageText}
                      onFocus={() => {
                        if (currentConversation?.conversationId && !currentConversation.conversationId.startsWith('temp-')) {
                          markAsRead(currentConversation.conversationId);
                        }
                        window.setTimeout(() => scrollToBottom(true), 350);
                      }}
                      onChange={(e) => {
                        setMessageText(e.target.value);
                        const convId = currentConversation?.conversationId;
                        if (convId && !convId.startsWith('temp-')) {
                          setTyping(convId, true);
                          if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
                          typingDebounceRef.current = setTimeout(() => {
                            setTyping(convId, false);
                            typingDebounceRef.current = null;
                          }, 4000);
                        }
                      }}
                      onKeyDown={() => {
                        const convId = currentConversation?.conversationId;
                        if (convId && !convId.startsWith('temp-')) {
                          setTyping(convId, true);
                          if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
                          typingDebounceRef.current = setTimeout(() => {
                            setTyping(convId, false);
                            typingDebounceRef.current = null;
                          }, 4000);
                        }
                      }}
                      onPaste={() => {
                        const convId = currentConversation?.conversationId;
                        if (convId && !convId.startsWith('temp-')) {
                          setTyping(convId, true);
                          if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
                          typingDebounceRef.current = setTimeout(() => {
                            setTyping(convId, false);
                            typingDebounceRef.current = null;
                          }, 4000);
                        }
                      }}
                      onBlur={() => {
                        const convId = currentConversation?.conversationId;
                        if (convId && !convId.startsWith('temp-')) {
                          setTyping(convId, false);
                        }
                      }}
                      placeholder="Type a message..."
                      className="chat-modal__composer-input"
                      disabled={isCreatingConversation || (!currentConversation?.otherUser?._id && !chatModalPreSelectedUser?._id)}
                      tabIndex={0}
                      aria-label="Message input"
                    />
                    <button
                      type="submit"
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={!messageText.trim() || isCreatingConversation || (!currentConversation?.otherUser?._id && !chatModalPreSelectedUser?._id)}
                      className="chat-modal__send-btn bg-[#f26522] text-white p-2 rounded-full hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      aria-label="Send message"
                    >
                      <FaPaperPlane className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="chat-modal__empty-chat">
                Select a conversation to start chatting
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
